import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { PatientCard } from '../components/PatientCard';
import { EmptyState } from '../components/EmptyState';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { APPOINTMENT_STATES } from '../utils/mockData';
import { format, startOfWeek, addDays } from 'date-fns';
import { es } from 'date-fns/locale';
import {
  Clock,
  Users,
  CheckCircle2,
  AlertCircle,
  Calendar,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';

import { API } from '@/lib/api';

export const DoctorDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [appointments, setAppointments] = useState([]);
  const [weekAppointments, setWeekAppointments] = useState([]);
  const [patients, setPatients] = useState([]);
  const [doctorProfile, setDoctorProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [weekOffset, setWeekOffset] = useState(0); // 0 = esta semana, -1 = anterior, +1 = siguiente

  const today = new Date().toISOString().split('T')[0];

  const weekDays = useMemo(() => {
    const base = addDays(startOfWeek(new Date(), { weekStartsOn: 1 }), weekOffset * 7);
    return Array.from({ length: 7 }, (_, i) => addDays(base, i));
  }, [weekOffset]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Find the doctor profile matching the logged-in user's email
        const [doctorsRes, patientsRes] = await Promise.all([
          axios.get(`${API}/doctors`),
          axios.get(`${API}/patients`, { params: { limit: 200 } }),
        ]);

        const allDoctors = doctorsRes.data;
        const doctor = allDoctors.find(d => d.email === user?.email);
        setDoctorProfile(doctor || null);
        setPatients(patientsRes.data);

        if (doctor) {
          const aptsRes = await axios.get(`${API}/appointments`, {
            params: { fecha: today, doctor_id: doctor.id, limit: 100 },
          });
          setAppointments(aptsRes.data);
        }
      } catch (err) {
        console.error('Error fetching doctor dashboard data', err);
        toast.error('Error al cargar el panel');
      } finally {
        setLoading(false);
      }
    };

    if (user) fetchData();
  }, [today, user]);

  // Carga citas de la semana usando fecha_inicio/fecha_fin (server-side)
  useEffect(() => {
    if (!doctorProfile) return;
    const fetchWeek = async () => {
      const fecha_inicio = format(weekDays[0], 'yyyy-MM-dd');
      const fecha_fin    = format(weekDays[6], 'yyyy-MM-dd');
      try {
        const res = await axios.get(`${API}/appointments`, {
          params: { doctor_id: doctorProfile.id, fecha_inicio, fecha_fin, limit: 500 },
        });
        setWeekAppointments(res.data);
      } catch { /* silencioso */ }
    };
    fetchWeek();
  }, [doctorProfile, weekDays]);

  const handleUpdateStatus = async (aptId, newStatus) => {
    try {
      await axios.put(`${API}/appointments/${aptId}/status`, null, {
        params: { estado: newStatus },
      });
      setAppointments(prev =>
        prev.map(a => a.id === aptId ? { ...a, estado: newStatus } : a)
      );
      const statusLabel = APPOINTMENT_STATES[newStatus]?.label || newStatus;
      toast.success(`Cita actualizada: ${statusLabel}`);
    } catch (err) {
      toast.error('Error al actualizar el estado de la cita');
    }
  };

  const handleViewPatient = (data) => {
    const patientId = data?.paciente_id || data?.id;
    navigate(`/patients/${patientId}`);
  };

  const getPatientForAppointment = (apt) => {
    return patients.find(p => p.id === apt.paciente_id);
  };

  const currentPending   = appointments.filter(a => a.estado === 'confirmada');
  const currentInWaiting = appointments.filter(a => a.estado === 'en_sala');
  const currentCompleted = appointments.filter(a => a.estado === 'atendido');
  const currentCancelled = appointments.filter(a => a.estado === 'cancelada');

  if (loading) {
    return (
      <Layout>
        <div className="space-y-6" data-testid="doctor-dashboard-loading">
          <Skeleton className="h-10 w-48" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-20 rounded-xl" />
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-32 rounded-xl" />
            ))}
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="doctor-dashboard">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight">Mi Panel</h1>
            <p className="text-muted-foreground mt-1">
              Bienvenido, {user?.nombre}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary/10">
              <Calendar className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium">
                {new Date().toLocaleDateString('es-ES', {
                  weekday: 'long',
                  day: 'numeric',
                  month: 'long'
                })}
              </span>
            </div>
          </div>
        </div>

        {/* No doctor profile linked */}
        {!doctorProfile && (
          <Card className="border-amber-200 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-900">
            <CardContent className="p-4 text-sm text-amber-700 dark:text-amber-400">
              No se encontró un perfil de doctor vinculado a tu cuenta ({user?.email}).
              Pide al administrador que cree un perfil de doctor con el mismo correo.
            </CardContent>
          </Card>
        )}

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-card border-border/50">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30">
                <Clock className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">{currentPending.length}</p>
                <p className="text-xs text-muted-foreground">Pendientes</p>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card border-border/50">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-100 dark:bg-amber-900/30">
                <Users className="h-5 w-5 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">{currentInWaiting.length}</p>
                <p className="text-xs text-muted-foreground">En Sala</p>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card border-border/50">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-emerald-100 dark:bg-emerald-900/30">
                <CheckCircle2 className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">{currentCompleted.length}</p>
                <p className="text-xs text-muted-foreground">Atendidos</p>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card border-border/50">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-rose-100 dark:bg-rose-900/30">
                <AlertCircle className="h-5 w-5 text-rose-600 dark:text-rose-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">{currentCancelled.length}</p>
                <p className="text-xs text-muted-foreground">Canceladas</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content - Kanban Style */}
        <Tabs defaultValue="all" className="w-full">
          <div className="overflow-x-auto -mx-1 px-1 pb-1">
            <TabsList className="mb-4 w-max">
              <TabsTrigger value="all" data-testid="tab-all">
                Hoy ({appointments.length})
              </TabsTrigger>
              <TabsTrigger value="pending" data-testid="tab-pending">
                Pendientes ({currentPending.length})
              </TabsTrigger>
              <TabsTrigger value="waiting" data-testid="tab-waiting">
                En Sala ({currentInWaiting.length})
              </TabsTrigger>
              <TabsTrigger value="completed" data-testid="tab-completed">
                Atendidos ({currentCompleted.length})
              </TabsTrigger>
              <TabsTrigger value="week" data-testid="tab-week">
                Semana
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="all">
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {appointments.map(apt => (
                <PatientCard
                  key={apt.id}
                  appointment={apt}
                  patient={getPatientForAppointment(apt)}
                  onViewPatient={handleViewPatient}
                  onUpdateStatus={handleUpdateStatus}
                />
              ))}
              {appointments.length === 0 && (
                <div className="col-span-full">
                  <EmptyState
                    icon={Calendar}
                    title="Sin citas hoy"
                    description="No tienes citas programadas para hoy. Disfruta del día tranquilo."
                  />
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="pending">
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {currentPending.map(apt => (
                <PatientCard
                  key={apt.id}
                  appointment={apt}
                  patient={getPatientForAppointment(apt)}
                  onViewPatient={handleViewPatient}
                  onUpdateStatus={handleUpdateStatus}
                />
              ))}
              {currentPending.length === 0 && (
                <div className="col-span-full">
                  <EmptyState icon={Clock} title="Sin pendientes" description="No hay citas pendientes por atender." />
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="waiting">
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {currentInWaiting.map(apt => (
                <PatientCard
                  key={apt.id}
                  appointment={apt}
                  patient={getPatientForAppointment(apt)}
                  onViewPatient={handleViewPatient}
                  onUpdateStatus={handleUpdateStatus}
                />
              ))}
              {currentInWaiting.length === 0 && (
                <div className="col-span-full">
                  <EmptyState icon={Users} title="Sala vacía" description="No hay pacientes en la sala de espera." />
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="completed">
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {currentCompleted.map(apt => (
                <PatientCard
                  key={apt.id}
                  appointment={apt}
                  patient={getPatientForAppointment(apt)}
                  onViewPatient={handleViewPatient}
                  onUpdateStatus={handleUpdateStatus}
                />
              ))}
              {currentCompleted.length === 0 && (
                <div className="col-span-full">
                  <EmptyState icon={CheckCircle2} title="Aún nadie atendido" description="Los pacientes que marques como atendidos aparecerán aquí." />
                </div>
              )}
            </div>
          </TabsContent>

          {/* ── Agenda semanal ─────────────────────────────────────────── */}
          <TabsContent value="week">
            <div className="space-y-3">
              {/* Navegación de semana */}
              <div className="flex items-center justify-between">
                <Button variant="outline" size="icon" onClick={() => setWeekOffset(w => w - 1)} aria-label="Semana anterior">
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm font-medium capitalize">
                  {format(weekDays[0], "d 'de' MMMM", { locale: es })} – {format(weekDays[6], "d 'de' MMMM, yyyy", { locale: es })}
                </span>
                <div className="flex items-center gap-2">
                  {weekOffset !== 0 && (
                    <Button variant="ghost" size="sm" onClick={() => setWeekOffset(0)} className="text-xs">
                      Esta semana
                    </Button>
                  )}
                  <Button variant="outline" size="icon" onClick={() => setWeekOffset(w => w + 1)} aria-label="Semana siguiente">
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* Días de la semana */}
              <div className="overflow-x-auto">
              <div className="grid grid-cols-7 gap-2 min-w-[560px]">
                {weekDays.map(day => {
                  const dateStr = format(day, 'yyyy-MM-dd');
                  const isToday = dateStr === today;
                  const dayApts = weekAppointments
                    .filter(a => a.fecha === dateStr && a.estado !== 'cancelada')
                    .sort((a, b) => a.hora_inicio.localeCompare(b.hora_inicio));
                  return (
                    <Card key={dateStr} className={`border-border/50 ${isToday ? 'ring-2 ring-primary/50' : ''}`}>
                      <CardHeader className="p-2 pb-1">
                        <p className={`text-xs font-semibold text-center capitalize ${isToday ? 'text-primary' : 'text-muted-foreground'}`}>
                          {format(day, 'EEE d', { locale: es })}
                        </p>
                      </CardHeader>
                      <CardContent className="p-2 pt-0 space-y-1 min-h-[80px]">
                        {dayApts.length === 0 ? (
                          <p className="text-[10px] text-muted-foreground text-center pt-2">Libre</p>
                        ) : dayApts.map(apt => (
                          <div
                            key={apt.id}
                            className="text-[10px] rounded px-1.5 py-1 bg-primary/10 border-l-2 border-primary leading-tight"
                          >
                            <p className="font-medium truncate">{apt.hora_inicio} {apt.paciente_nombre}</p>
                            <p className="text-muted-foreground truncate">{apt.motivo}</p>
                          </div>
                        ))}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
              </div>

              {/* Resumen de la semana */}
              <p className="text-xs text-muted-foreground text-right">
                {weekAppointments.filter(a => a.estado !== 'cancelada').length} citas esta semana
              </p>
            </div>
          </TabsContent>
        </Tabs>

        {/* Quick Action - Next Patient */}
        {currentInWaiting.length > 0 && (
          <Card className="bg-gradient-to-r from-primary/10 to-secondary/10 border-primary/20">
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <h3 className="font-semibold text-lg">Siguiente Paciente</h3>
                  <p className="text-muted-foreground">
                    {currentInWaiting[0].paciente_nombre} - {currentInWaiting[0].motivo}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => handleViewPatient(currentInWaiting[0])}
                  >
                    Ver Expediente
                  </Button>
                  <Button onClick={() => handleUpdateStatus(currentInWaiting[0].id, 'atendido')}>
                    Marcar como Atendido
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
};
